import { useEffect, useRef, type RefObject } from "react";

/** Past this much of a page, a release commits to the next page instead of settling
 *  back — a decisive pull, not a full one. */
const COMMIT_FRACTION = 0.18;
/** Or this fast, whatever the distance: a flick. Pixels per millisecond. */
const FLICK_PX_PER_MS = 0.6;
/** Under this the pointer never really moved: a click, and the click is let through. */
const DRAG_SLOP_PX = 8;
/** How long the commit scroll is given before snapping is switched back on. */
const SETTLE_MS = 500;
/** A click this soon after a moved release is the drag's own, and is dropped. */
const CLICK_AFTER_DRAG_MS = 250;

/**
 * A MOUSE grabs the scroll and pulls it, the way a finger does on a phone.
 *
 * Native scroll-snap gives a finger momentum and a wheel its notches, but a mouse
 * button held down on a page is nothing to the browser. Here it is a grab: while the
 * button is down the scroller follows the pointer (snapping is paused for the hold —
 * mandatory snap re-snaps on every programmatic scroll and would fight the hand), and
 * on release the pull is judged like a swipe: far enough or fast enough commits to the
 * next page, anything less settles back. The commit is a smooth scroll to the page's
 * exact top, and snapping returns once it has landed.
 *
 * A drag that moved is not a click. The stage's tiles toggle sound on click, and the
 * browser may still fire one after a press-move-release, so a click arriving within a
 * beat of a moved release is swallowed in the capture phase. A WINDOW, not a flag:
 * under pointer capture the release lands on the scroller rather than the tile and
 * often no click comes at all, and a flag left armed would eat the next honest one.
 *
 * Mouse only. A finger has the browser's own scrolling, and a pen is a finger here.
 * The keys are the desk's other hand: Arrow Up/Down and Page Up/Down page too.
 *
 * AXIS-LOCKED at the first real move: a pull that starts more sideways than down is
 * the screen's channel swipe (useHorizontalSwipe on the glass above), and this hook
 * lets go of it entirely — no capture, no scroll, no click swallowed here.
 *
 * Returns `step` (2026-09-14): the same page move the keys make, for a caller that pages
 * on its own clock — auto-scroll (useAutoScroll). Stable across renders and reading the
 * element at call time, so it is safe to hold in a timer.
 */
export function useDragPaging(ref: RefObject<HTMLElement | null>): { step: (dir: 1 | -1) => void } {
  const settleRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const stepRef = useRef<(dir: 1 | -1) => void>(() => undefined);
  const pageHeightOf = (el: HTMLElement) => el.clientHeight || 1;
  const goTo = (el: HTMLElement, page: number) => {
    const pages = Math.max(1, Math.round(el.scrollHeight / pageHeightOf(el)));
    const target = Math.max(0, Math.min(pages - 1, page)) * pageHeightOf(el);
    el.style.scrollSnapType = "none";
    el.scrollTo({ top: target, behavior: "smooth" });
    if (settleRef.current) clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => {
      el.style.scrollSnapType = "";
    }, SETTLE_MS);
  };
  stepRef.current = (dir) => {
    const el = ref.current;
    if (el) goTo(el, Math.round(el.scrollTop / pageHeightOf(el)) + dir);
  };
  const stepStable = useRef((dir: 1 | -1) => stepRef.current(dir)).current;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let drag: { id: number; x: number; y: number; top: number; at: number; moved: boolean } | null = null;
    let swallowUntil = 0;

    const pageHeight = () => pageHeightOf(el);
    const step = (dir: 1 | -1) => stepStable(dir);

    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      // Not from the crown, and not from anything else that is a control of its own.
      if (e.target instanceof Element && e.target.closest("button, a, input")) return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, top: el.scrollTop, at: performance.now(), moved: false };
      if (settleRef.current) clearTimeout(settleRef.current);
      el.style.scrollSnapType = "none";
      // NOT captured yet. A click's target is the common ancestor of where the button
      // went down and where it came up; capturing here would make every release land
      // on the scroller and no tile would ever see its click. The capture waits for a
      // real move (below), when the press has already stopped being a click.
      // No text selection, no image drag, while the hand is on the glass.
      e.preventDefault();
    };
    const onMove = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.id) return;
      const dy = e.clientY - drag.y;
      if (!drag.moved) {
        const dx = e.clientX - drag.x;
        if (Math.abs(dy) < DRAG_SLOP_PX && Math.abs(dx) < DRAG_SLOP_PX) return;
        if (Math.abs(dx) > Math.abs(dy)) {
          // Sideways: the channel swipe's, not ours.
          drag = null;
          el.style.scrollSnapType = "";
          return;
        }
        drag.moved = true;
        el.setPointerCapture(e.pointerId);
      }
      el.scrollTop = drag.top - dy;
    };
    const onUp = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.id) return;
      const d = drag;
      drag = null;
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      if (!d.moved) {
        el.style.scrollSnapType = "";
        return;
      }
      swallowUntil = performance.now() + CLICK_AFTER_DRAG_MS;
      const dy = e.clientY - d.y;
      const dt = Math.max(1, performance.now() - d.at);
      const from = Math.round(d.top / pageHeight());
      const far = Math.abs(dy) >= pageHeight() * COMMIT_FRACTION;
      const fast = Math.abs(dy) / dt >= FLICK_PX_PER_MS;
      goTo(el, far || fast ? from + (dy < 0 ? 1 : -1) : from);
    };
    const onCancel = () => {
      drag = null;
      el.style.scrollSnapType = "";
    };
    const onClick = (e: MouseEvent) => {
      if (performance.now() > swallowUntil) return;
      swallowUntil = 0;
      e.stopPropagation();
      e.preventDefault();
    };
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName)) return;
      // Not under a sheet or a dialog: paging the wall behind the picker reads as a
      // haunting, the same gate the screen's Left/Right keep.
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === "ArrowDown" || e.key === "PageDown") step(1);
      else if (e.key === "ArrowUp" || e.key === "PageUp") step(-1);
      else return;
      e.preventDefault();
    };

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onCancel);
    el.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKey);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onCancel);
      el.removeEventListener("click", onClick, true);
      window.removeEventListener("keydown", onKey);
      if (settleRef.current) clearTimeout(settleRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);
  return { step: stepStable };
}
